import React, { useRef, useState, useEffect } from "react";
import { Container, Nav, NavItem, NavLink } from "reactstrap";
import classnames from "classnames";
import studyTabsStyles from "../css/studytabs.module.scss";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { STUDY_PATHS } from "Routes/paths";
import CommonTabHeader from "Components/Common/CommonTabHeader";
import StudyICF from "../../StudyICF/pages/StudyICF";
import StudyTestList from "../../StudyTestList/pages/StudyTestList";
import OutOfRangeParametersPage from "../../StudyTestList/components/OutOfRangeParametersPage";
import ScreeningList from "../../StudyScreening/pages/StudyScreeningList";
import { useVolunteerStudyICF } from "modules/Volunteer/AssigningStudyModule/StudyICF/api/useVolunteerStudyICF";
import { StudyTabKey } from "../helper/studyscreeningtabs.types";
import { VolunteerStudyScreeningTabs } from "../helper/studyscreening.constant";
import RouteGuard from "Routes/RouteGuard";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import StudyDetails from "modules/Clinical/StudyDetails/pages/StudyDetails";

/* --------------------------------
 Types
--------------------------------- */



/* ---------------- MAIN COMPONENT ---------------- */

const StudyScreeningTabs: React.FC = () => {
  document.title = "Study Screening | ZENTIXS VISTA";

  const [activeTab, setActiveTab] = useState<StudyTabKey>("detail");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [numberofsubject, setnumberofsubject] = useState<number>(0);
  const toggle = (): void => setIsOpen((prev) => !prev);
  const studyName = sessionStorage.getItem("studycode") || "";
  const activeStudyVolunteerScreeningStatus = JSON.parse(
    sessionStorage.getItem("active_volunteer_screening_status") || "",
  );
  const volunteerName = sessionStorage.getItem("volunteername") || "";
  const segmentTabsRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const volunteerid = Number(sessionStorage.getItem("activevolunteerid"));
  const studyid = Number(sessionStorage.getItem("activestudyid"));

  const { SelectAllQuery } = useVolunteerStudyICF({
    volunteerid,
    studyid,
  });

  ;

  /* ---------- Slider ---------- */

  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [slider, setSlider] = useState<{ left: string; width: string }>({
    left: "2px",
    width: "80px",
  });

  /* ---------- Sync tab from URL ---------- */

  useEffect(() => {
    if (location.pathname.includes("outofparameter")) {
      setActiveTab("test");
      return;
    }
    const last = location.pathname.split("/").filter(Boolean).pop();
    const match = VolunteerStudyScreeningTabs.find((t) => t.key === last);

    setActiveTab(match ? match.key : "detail");
  }, [location.pathname]);

  /* ---------- Tab click ---------- */

  const toggleTab = (tabKey: StudyTabKey, index: number): void => {
    navigate(tabKey);
    setActiveTab(tabKey);

    const el = tabRefs.current[index];
    const padding = segmentTabsRef.current
      ? parseInt(getComputedStyle(segmentTabsRef.current).paddingLeft)
      : 0;

    if (el) {
      setSlider({
        left: el.offsetLeft + padding + "px",
        width: el.offsetWidth + "px",
      });
    }
  };

  /* ---------- Slider reposition ---------- */

  useEffect(() => {
    const index = VolunteerStudyScreeningTabs.findIndex((t) => t.key === activeTab);
    const el = tabRefs.current[index];

    const containerPadding = segmentTabsRef.current
      ? parseInt(getComputedStyle(segmentTabsRef.current).paddingLeft)
      : 0;

    if (el) {
      setSlider({
        left: el.offsetLeft + containerPadding + "px",
        width: el.offsetWidth + "px",
      });
    }
  }, [activeTab]);

  const isTabLocked = (tabKey: StudyTabKey): boolean => {
    if (tabKey === "screening" || tabKey === "test") {
      const icfData = SelectAllQuery?.data?.data?.[0];
      const hasICFData = !!(icfData?.checklist?.length > 0);
      return !hasICFData;
    }
    return false;
  };

  /* ---------------- RENDER ---------------- */

  return (
    <Container fluid>
      <div className={studyTabsStyles.study_tab_wrapper}>
        <CommonTabHeader
          title={`${volunteerName} ${volunteerName && studyName ? "| " + studyName : studyName} ${activeStudyVolunteerScreeningStatus ? "| " + activeStudyVolunteerScreeningStatus.toUpperCase() : ""}`}
          isOpen={isOpen}
          toggle={toggle}
          activeTab={activeTab}
          exitPath={STUDY_PATHS.SCREENING_LIST}
        />

        <div
          className={`d-flex justify-content-between align-items-center bg-white mb-3 ${studyTabsStyles.study_tab_container}`}
        >
          {/* ===== SEGMENTED TAB NAV ===== */}
          <Nav
            innerRef={segmentTabsRef}
            className={studyTabsStyles.segmentTabs}
          >
            <div
              className={studyTabsStyles.slider}
              style={
                {
                  "--left": slider.left,
                  "--width": slider.width,
                } as React.CSSProperties
              }
            />

            {VolunteerStudyScreeningTabs.map((t, index) => (
              <NavItem key={t.key}>
                <NavLink
                  innerRef={(el) => {
                    tabRefs.current[index] = el;
                  }}
                  className={classnames(studyTabsStyles.tab, {
                    [studyTabsStyles.active]: activeTab === t.key,
                    [studyTabsStyles.disabledTab]: isTabLocked(t.key),
                  })}
                  onClick={() => {
                    if (isTabLocked(t.key)) return;
                    toggleTab(t.key, index);
                  }}
                  style={{
                    // pointerEvents: "auto",
                    // opacity: 1,
                    // cursor: "pointer",
                    pointerEvents: isTabLocked(t.key) ? "none" : "auto",
                    opacity: isTabLocked(t.key) ? 0.5 : 1,
                    cursor: isTabLocked(t.key) ? "not-allowed" : "pointer",
                  }}
                >
                  <i className={`${t.icon} me-1 ${studyTabsStyles.icon}`} />
                  <span>{t.label}</span>
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </div>
      </div>
      {/* ===== ROUTES ===== */}
      <Routes>
        <Route
          index
          element={<RouteGuard module={PERMISSION_MODULES.STUDY_SCREENING} action={PERMISSION_ACTIONS.VIEW} ><StudyDetails setnumberofsubject={setnumberofsubject} /></RouteGuard>}
        />
        <Route path="icf" element={<RouteGuard module={PERMISSION_MODULES.STUDY_SCREENING} action={PERMISSION_ACTIONS.VIEW} ><StudyICF /></RouteGuard>} />
        <Route path="screening" element={<RouteGuard module={PERMISSION_MODULES.STUDY_SCREENING} action={PERMISSION_ACTIONS.VIEW} ><ScreeningList /></RouteGuard>} />
        <Route path="test" element={<RouteGuard module={PERMISSION_MODULES.STUDY_SCREENING} action={PERMISSION_ACTIONS.VIEW} ><StudyTestList /></RouteGuard>} />
        <Route path="test/outofparameter/:studylabtestid" element={<RouteGuard module={PERMISSION_MODULES.STUDY_SCREENING} action={PERMISSION_ACTIONS.VIEW} ><OutOfRangeParametersPage /></RouteGuard>} />
        <Route
          path="*"
          element={<RouteGuard module={PERMISSION_MODULES.STUDY_SCREENING} action={PERMISSION_ACTIONS.VIEW} ><StudyDetails setnumberofsubject={setnumberofsubject} /></RouteGuard>}
        />
      </Routes>
    </Container>
  );
};

export default StudyScreeningTabs;
